import {
  Device,
  Document,
  DocumentAnswer,
  DocumentAnswerType,
  DocumentVersion,
  DocumentVersionApproverStatus,
  RoadmapTask,
  RoadmapTasks,
  User,
} from "@models";
import {
  EditOutlined,
  FiberManualRecord,
  FileCopyOutlined,
  HistoryEdu,
  LockOutlined,
  NoteAddOutlined,
  TaskOutlined,
} from "@mui/icons-material";
import { ChipOwnProps, IconProps } from "@mui/material";
import { chain, orderBy, uniq } from "lodash";
import { getOrgFromUser, getUserDisplayName } from "src/utils/user";
import { Entries } from "type-fest";
import { templatesWithUpload } from "../config";
import {
  DataKeyWithValue,
  DocStatus,
  DocUIStatus,
  DocumentStatus,
  DocumentUIStatus,
  DocVersionWithStatusAndRevision,
  Template,
  TemplateConfig,
  TemplateQuestion,
} from "../types";
import { meetsVisibilityConditions } from "./roadmap-visibilty-conditions";
import { captureSentryException } from "./sentry";

export const getDocTemplate = (
  templateKey: string,
  templatesConfig: TemplateConfig
) => {
  if (templatesConfig[templateKey]) {
    return templatesConfig[templateKey];
  } else {
    return undefined;
  }
};

/**
 * Get all template types where the respective documents are not completed but specified as required context by one of the elements in in given template type
 */
export const getIncompleteContexts = (
  templateKey: string,
  documents: Document[],
  user: User,
  orgId: string,
  templatesConfig: TemplateConfig
): string[] => {
  const templateConfig = templatesConfig[templateKey];
  if (!templateConfig) return [];
  const { questions } = templateConfig;

  const configOfRelatedDocuments: string[] = uniq(
    questions
      .map((question) => question.context || [])
      .flat()
      .map(
        (dataKey) =>
          Object.entries(templatesConfig).find(
            ([k, v]) =>
              k !== templateKey &&
              v.questions.map((e) => e.id).includes(dataKey)
          )?.[0] || []
      )
      .flat()
  );

  // Check if every document that is mentioned inside the context config in the TemplateConfig is present in the documents array and marked as completed
  return configOfRelatedDocuments.filter((documentId) => {
    const document = documents.find((d) => d.templateKey === documentId);
    if (!document) return true;

    const docStatus = getDocTypeStatus({
      templateKey: documentId,
      documents,
      user: user,
      orgId: orgId,
    });

    if (docStatus === "DRAFT" || docStatus === "AVAILABLE") {
      return true;
    }

    return false;
  });
};

/**
 * RCD documents with a templateKey always show record UI.
 * Non-RCD documents show record UI only when 2+ docs already share the same templateKey.
 *
 * Note: From the backend perspective: all documents can have multiple instances, rcd isn't special.
 * From a UI perspective we need to work on supporting this, but for now, this makes it so
 * we can easily scale up into supporting it and hide the "Create Record" button for anything but records.
 */
export const canDocHaveMultipleInstances = ({
  document,
  documents,
}: {
  document?: Document;
  documents: Document[];
}) => {
  if (!document || !document.templateKey) return false;
  // RCD docs with a templateKey always show record UI
  if (document.regulatoryType === "RCD") return true;
  // Non-RCD: only if 2+ docs already share this templateKey
  return (
    documents.filter((d) => d.templateKey === document.templateKey).length >= 2
  );
};

// Use this to find all instances of a document in the document list
// Checks if props are the same and groups them together
export const findInstancesOfTemplateKey = (
  document: Document,
  documents: Document[]
) =>
  orderBy(
    documents.filter((doc) => doc.templateKey === document.templateKey),
    "createdAt",
    "desc"
  );

export const findDocInstancesFromTemplate = (
  documents: Document[],
  templateKey: string
) =>
  orderBy(
    documents?.filter((doc) => doc.templateKey === templateKey),
    "createdAt",
    "desc"
  );

export const checkIfDocHasDraft = (
  document: Document,
  user: User,
  orgId: string
) =>
  getDocVersionsWithStatus({
    document,
    user,
    orgId,
  }).some((s) => s.status === "DRAFT");

/**
 * Get all dependencies that are not completed
 * IMPORTANT this does not check if required context is completed or other dependencies.
 * Refer to * isDocumentUnlocked for that.
 * @param templateType  TEMPLATE_ID
 * @param documents Document[]
 * @returns (string)[]
 */
export const getIncompleteDependencies = (
  templateType: string,
  documents: Document[],
  templatesConfig: TemplateConfig
): string[] => {
  const templateConfig = templatesConfig[templateType];
  if (!templateConfig) return [];

  const incompleteDependencies: string[] = [];

  // Check if every document that is mentioned inside the dependencies in the TemplateConfig is present in the documents array and marked as completed
  templateConfig.dependencies?.forEach((dependency) => {
    const templateRelatedToDependency = Object.entries(templatesConfig).find(
      ([_, value]) =>
        value.questions.some((element) => element.id === dependency)
    )?.[0];

    if (!templateRelatedToDependency) {
      throw new Error(
        `Could not find template related to dependency ${dependency}`
      );
    }

    const isDependencyDocumentComplete =
      documents.find((d) => d.templateKey === templateRelatedToDependency)
        ?.versions[0]?.readyForApproval === true;

    if (!isDependencyDocumentComplete) {
      incompleteDependencies.push(templateRelatedToDependency);
    }
  });

  return incompleteDependencies;
};

export const isDocumentHidden = (
  templateKey: string,
  device: Device,
  templatesConfig: TemplateConfig,
  tasks: RoadmapTasks,
  documents: Document[]
) => {
  const templateConfig = templatesConfig[templateKey];
  if (!templateConfig) {
    return false;
  }

  if (templateConfig.hidden === true) {
    return true;
  }
  // get the visibility of the document from the tasks config
  const task = tasks?.find((task) => task.taskKey === templateKey);

  if (task?.visibilityConditions) {
    return !meetsVisibilityConditions(
      task.visibilityConditions,
      device,
      documents
    );
  }

  return false;
};

export type getDocumentContexts = {
  missingDependencies: string[];
  missingContexts: string[];
  incompleteTemplates: string[];
};

export const getDocumentContexts = ({
  templateKey,
  documents,
  user,
  orgId,
  templatesConfig,
}: {
  templateKey: string;
  documents: Document[];
  user: User;
  orgId: string;
  templatesConfig: TemplateConfig;
}): getDocumentContexts => {
  if (!templatesConfig[templateKey]) {
    return {
      missingDependencies: [],
      missingContexts: [],
      incompleteTemplates: [],
    };
  }

  const missingContexts = getIncompleteContexts(
    templateKey,
    documents,
    user,
    orgId,
    templatesConfig
  );

  const missingDependencies = getIncompleteDependencies(
    templateKey,
    documents,
    templatesConfig
  );

  return {
    missingDependencies,
    missingContexts,
    incompleteTemplates: uniq([...missingDependencies, ...missingContexts]),
  };
};

/**
 * Takes the answers from the document version and returns an object with the answers as values and the corresponding data keys as keys
 */
export const getDocumentAnswerMap = (documentVersion: DocumentVersion) => {
  const mappedAnswers: Partial<Record<string, DocumentAnswer>> = {};

  if (Array.isArray(documentVersion.answers)) {
    documentVersion.answers.forEach((answer) => {
      mappedAnswers[answer.element] = answer;
    });
  }

  return mappedAnswers;
};

export const getDocumentStepsFromConfig = (
  templateConfig: Template
): TemplateQuestion[] => {
  return templateConfig.questions;
};

export const getFilteredElements = (
  elements: TemplateQuestion[],
  device: Device,
  documents: Document[]
): TemplateQuestion[] => {
  return elements.filter((element) => {
    return meetsVisibilityConditions(
      element.visibilityConditions,
      device,
      documents
    );
  });
};

export const isStaticallyGeneratedDocument = (
  templateConfig: Template
): boolean => !hasDocInputs(templateConfig) && !templateConfig.userUpload;

export const hasDocInputs = (templateConfig: Template): boolean => {
  return templateConfig.questions.length > 0;
};

export const getTemplateConfig = ({
  templateKey,
  templatesConfig,
  extraContext,
}: {
  templateKey: string;
  templatesConfig: TemplateConfig;
  extraContext?: Record<string, any>;
}): Template => {
  const template = templatesConfig[templateKey];
  if (!template) {
    captureSentryException(
      `Template "${templateKey}" not found in configuration`,
      { extra: { templateKey, templatesConfig, extraContext } }
    );
    throw new Error(`Template "${templateKey}" not found in configuration`);
  }
  return template;
};

export const getElementConfigByDataKey = (
  dataKey: string,
  templatesConfig: TemplateConfig
): TemplateQuestion => {
  const docConfigs = Object.entries(templatesConfig) as Entries<TemplateConfig>;
  // Get the document config that contains the element with the dataKey
  const docConfigOfElement = docConfigs.find(([_, config]) =>
    config.questions.some((element) => element.id === dataKey)
  );
  if (!docConfigOfElement) {
    throw new Error(`Could not find document config for dataKey ${dataKey}`);
  }
  // Get the element config that has the dataKey
  const elementConfig = docConfigOfElement[1].questions.find(
    (element) => element.id === dataKey
  );
  if (!elementConfig) {
    throw new Error(`Could not find element config for dataKey ${dataKey}`);
  }
  return elementConfig;
};

export const loadDocumentReferences = (
  documents: Document[],
  templatesConfig: TemplateConfig
): Record<string, string> => {
  const referenceObject: Record<string, string> = {};

  const docConfigs = Object.entries(templatesConfig) as Entries<TemplateConfig>;
  docConfigs.forEach(([key]) => {
    const document = documents.find((d) => d.templateKey === key);
    const templateConfig = templatesConfig[key];
    if (!document || !document.versions[0]) {
      referenceObject[`did-${document?.templateKey}`] = "Reference not found.";
      return;
    }
    const documentIdentifier = getDocumentIdentifier(
      document,
      document.versions[0],
      document.versions,
      templateConfig
    );

    referenceObject[`did-${document.templateKey}`] = documentIdentifier;
  });

  return referenceObject;
};

export const getDocumentIdentifier = (
  document: Document,
  documentVersion: DocumentVersion,
  documentVersions: DocumentVersion[],
  templateConfig: Template
) => {
  const { defaultTitle, regulatoryType, functionalGroupAcronym } =
    templateConfig;
  const documentNumber = document.id;
  const revision = getDocVersionRevisionNumber(
    documentVersion,
    documentVersions
  );
  return `${functionalGroupAcronym || "ungrouped"}-${regulatoryType}-${documentNumber}-R${revision}-${defaultTitle}`;
};

export const isRevision = (docVersion: DocumentVersion) =>
  typeof docVersion.revision === "number";

export const hasRevisionNumber = (
  docVersion?: DocumentVersion | null
): docVersion is DocumentVersion & { revision: number } =>
  typeof docVersion?.revision === "number";

export const hasDocumentRevision = (document: Document) =>
  (document.latestRevisionNumber ?? 0) > 0 ||
  document.versions.some(hasRevisionNumber);

export const shouldShowSignatureTableByDefault = (
  docVersion?: DocumentVersion | null
) => hasRevisionNumber(docVersion);

export const isDocRevisionLive = (
  docRevision: DocumentVersion,
  docVersions: DocumentVersion[]
) => {
  if (typeof docRevision.revision !== "number") return false;

  const allRevisions = chain(docVersions)
    .filter((v) => typeof v.revision === "number")
    .orderBy("revision", "desc")
    .value();

  if (allRevisions.length === 0) return false;

  const isLatestRevision = allRevisions[0]?.id === docRevision.id;
  return isLatestRevision;
};

/**
 * Returns the relevant version for a document: the live revision if one exists,
 * otherwise the first (latest) version. Used across data key features.
 */
export const getRelevantDocVersion = (
  doc: Document
): DocumentVersion | undefined => {
  return (
    doc.versions.find((v) => isDocRevisionLive(v, doc.versions)) ??
    doc.versions[0]
  );
};

/**
 * Resolves the answer value for a given data key element from a document.
 * Looks up the relevant version and finds the matching answer.
 * Returns empty string if not found.
 */
export const getDocumentAnswerValue = (
  doc: Document,
  element: string
): string => {
  const version = getRelevantDocVersion(doc);
  if (!version) return "";
  const answer = version.answers.find((a) => a.element === element);
  return answer?.answer ?? "";
};

export const isReadyForApproval = (docVersion: DocumentVersion) =>
  docVersion.readyForApproval;

export const isDraft = (docVersion: DocumentVersion) =>
  docVersion.readyForApproval === false;

export const isDocRevisionArchived = (
  docVersion: DocumentVersion,
  docVersions: DocumentVersion[]
) => {
  if (typeof docVersion.revision !== "number") return false;

  // If it is not the latest revision then it is archived
  const allRevisions = chain(docVersions)
    .filter((v) => typeof v.revision === "number")
    .orderBy("revision", "desc")
    .value();

  if (allRevisions.length <= 1) return false;

  return allRevisions[0]?.id !== docVersion.id;
};

export const canPrecreateDocumentVersion = (templateConfig: Template) =>
  !templateConfig.userUpload;

export const getDocVersionsWithStatus = ({
  document,
  user,
  orgId,
}: {
  document: Document;
  user: User;
  orgId: string;
}): DocVersionWithStatusAndRevision[] => {
  return document.versions.map((version) => ({
    ...version,
    status: getDocVersionStatus({
      selectedDocVersion: version,
      document,
      user,
      orgId,
    }),
    revision: getDocVersionRevisionNumber(version, document.versions),
  }));
};

export const canUserDeleteDraft = (docVersion: DocumentVersion) => {
  return !isRevision(docVersion);
};

/** Get the status of all document versions
 * across all documents with the same templateKey (name)
 * and return the lowest status
 * AVAILABLE < DRAFT < UNAPPROVED < LIVE
 */
export const getDocTypeStatus = ({
  documents,
  templateKey,
  user,
  orgId,
}: {
  documents: Document[];
  templateKey: string;
  user: User;
  orgId: string;
}): DocStatus => {
  const documentsOfDocType = documents.filter(
    (doc) => doc.templateKey === templateKey
  );
  let initialStatus = DocumentStatus.LIVE;
  const isEmptyDoc = documentsOfDocType.length === 0;
  const hasNoVersion =
    !isEmptyDoc && documentsOfDocType[0].versions.length === 0;

  if (isEmptyDoc || hasNoVersion) {
    initialStatus = getDocVersionStatus({
      document: documentsOfDocType[0],
      user,
      orgId,
    });
  }

  const allDocVersions = documentsOfDocType
    .map((doc) => {
      return getDocVersionsWithStatus({
        document: doc,
        user,
        orgId,
      });
    })
    .flat();

  // AVAILABLE < DRAFT < UNAPPROVED < LIVE
  let lowestStatus = initialStatus;

  // update lowestStatus if docVersion.status is lower
  const someUnapproved = allDocVersions.some(
    (docVersion) => docVersion.status === DocumentStatus.UNAPPROVED
  );
  if (someUnapproved) {
    lowestStatus = DocumentStatus.UNAPPROVED;
  }

  const someDraft = allDocVersions.some(
    (docVersion) => docVersion.status === DocumentStatus.DRAFT
  );
  if (someDraft) {
    lowestStatus = DocumentStatus.DRAFT;
  }

  const someAvailable = allDocVersions.some(
    (docVersion) => docVersion.status === DocumentStatus.AVAILABLE
  );
  if (someAvailable) {
    lowestStatus = DocumentStatus.AVAILABLE;
  }

  return lowestStatus;
};

/**
 * Traverses incomplete document contexts with one shared visited set.
 *
 * Document contexts form a graph rather than a guaranteed tree, so a valid
 * A -> B -> A relationship must terminate without hiding either incomplete
 * template from the result.
 */
const collectIncompleteDependencies = (
  templateKey: string,
  documents: Document[],
  user: User,
  orgId: string,
  templatesConfig: TemplateConfig,
  tasks: RoadmapTasks,
  visitedTemplateKeys: Set<string>
): string[] => {
  // Mark the node before visiting descendants so the second A in A -> B -> A
  // stops immediately while the parent still reports A as incomplete.
  if (visitedTemplateKeys.has(templateKey)) {
    return [];
  }
  visitedTemplateKeys.add(templateKey);

  // We want to check if the document is unlocked
  const { incompleteTemplates } = getDocumentContexts({
    templateKey,
    documents,
    user,
    orgId,
    templatesConfig,
  });

  if (!incompleteTemplates || incompleteTemplates.length === 0) {
    return [];
  }

  let allLockedDocs: string[] = [...incompleteTemplates];

  // Recursively check each incomplete document
  incompleteTemplates.forEach((docId) => {
    const nestedLocked = collectIncompleteDependencies(
      docId,
      documents,
      user,
      orgId,
      templatesConfig,
      tasks,
      visitedTemplateKeys
    );
    allLockedDocs = [...allLockedDocs, ...nestedLocked];
  });

  return [...new Set(allLockedDocs)];
};

export const getIncompleteDependenciesRecursively = (
  templateKey: string,
  documents: Document[],
  user: User,
  orgId: string,
  templatesConfig: TemplateConfig,
  tasks: RoadmapTasks
): string[] => {
  // Each top-level calculation needs independent traversal state because this
  // helper is called repeatedly for different documents during the same render.
  return collectIncompleteDependencies(
    templateKey,
    documents,
    user,
    orgId,
    templatesConfig,
    tasks,
    new Set()
  );
};

export const getDocVersionStatus = ({
  selectedDocVersion,
  document,
  user,
  orgId,
}: {
  //the doc might not have been created yet therefore it might not have
  //a selectedDocVersion and a document
  selectedDocVersion?: DocumentVersion;
  document: Document;
  user: User;
  orgId: string;
}): DocStatus => {
  const org = getOrgFromUser(user, orgId);

  if (!org) {
    throw new Error("Org not found");
  }

  if (selectedDocVersion) {
    const isArchived = isDocRevisionArchived(
      selectedDocVersion,
      document.versions
    );
    if (isArchived) {
      return DocumentStatus.ARCHIVED;
    } else if (isDocRevisionLive(selectedDocVersion, document.versions)) {
      return DocumentStatus.LIVE;
    } else if (isReadyForApproval(selectedDocVersion)) {
      return DocumentStatus.UNAPPROVED;
    } else if (isDraft(selectedDocVersion)) {
      return DocumentStatus.DRAFT;
    }
  }

  if (!selectedDocVersion) {
    return DocumentStatus.AVAILABLE;
  }
  throw new Error(
    `Document status could not be determined: ${document.templateKey}`
  );
};

export const isUploadDoc = (
  templateType: string,
  templatesConfig: TemplateConfig
) => templatesWithUpload(templatesConfig).includes(templateType);

export const getDocVersionRevisionNumber = (
  docVersion: DocumentVersion,
  allDocVersionsOfSameDoc: DocumentVersion[]
): number => {
  if (typeof docVersion.revision === "number") {
    return docVersion.revision + 1;
  } else {
    // Check if there are any other revisions and if yes then return the highest revision number + 1
    const revisions = allDocVersionsOfSameDoc.filter(
      (v) => typeof v.revision === "number"
    );

    const isFirstRevision = revisions.length === 0;
    if (isFirstRevision) return 1;
    const highestRevision = Math.max(
      ...revisions.map((v) => v.revision as number)
    );

    return highestRevision + 2;
  }
};

export const getPreviousVersion = (
  selectedVersion: DocumentVersion,
  selectedDoc: Document
): DocumentVersion | null => {
  const currentIndex = selectedDoc.versions.findIndex(
    (v) => v.id === selectedVersion.id
  );

  if (currentIndex === -1 || selectedDoc.versions.length === 1) {
    return null;
  }

  return selectedDoc.versions[currentIndex + 1] || null;
};

export const getDependantTemplates = (
  changedAnswersIds: string[],
  templatesConfig: TemplateConfig
): [string, string][] => {
  const templateKeyAndTemplatePairs = Object.entries(templatesConfig) as [
    string,
    Template,
  ][];

  const directDependants = templateKeyAndTemplatePairs
    .filter(([_, template]) => {
      if (!template || !template.questions) return false;

      const hasUpdatedIdInContext = template.questions.some((element) => {
        const contextExists = "context" in element;
        return (
          contextExists &&
          element.context?.some((contextId) =>
            changedAnswersIds.includes(contextId)
          )
        );
      });

      const hasUpdatedIdInDependencies =
        template.dependencies?.some((dependencyId) =>
          changedAnswersIds.includes(dependencyId)
        ) ?? false;

      return hasUpdatedIdInContext || hasUpdatedIdInDependencies;
    })
    .map(([templateKey, template]): [string, string] => [
      templateKey,
      template.defaultTitle,
    ]);

  return directDependants;
};

export const getRecentlyUpdatedElementIds = (
  newDocVersion: DocumentVersion,
  secondToLastVersion: DocumentVersion | undefined
): string[] => {
  if (!secondToLastVersion) {
    return [];
  }

  return newDocVersion.answers
    .filter((newAnswer) => {
      const previousElement = secondToLastVersion.answers.find(
        (a) => a.element === newAnswer.element
      );
      const hasAnswerChanged = newAnswer.answer !== previousElement?.answer;
      const hasAnswerFileIdChanged =
        newAnswer.answerFileId !== previousElement?.answerFileId;

      return previousElement && (hasAnswerChanged || hasAnswerFileIdChanged);
    })
    .map((answer) => answer.element);
};

export const hasRevisionOneOreMoreApprovals = (docVersion: DocumentVersion) => {
  return docVersion.documentVersionApprover.some(
    (a) => a.approvalStatus === DocumentVersionApproverStatus.APPROVED
  );
};

export const isDocVersionAuthor = (version: DocumentVersion, user: User) =>
  version.createdBy === user.id;

export const getAvailableDataKeys = (
  documents: Document[]
): DataKeyWithValue[] => {
  return documents
    .map((doc): DataKeyWithValue[] => {
      // Get the live version of if no live version exist the latest draft
      const relevantDoc =
        doc.versions.find((v) => isDocRevisionLive(v, doc.versions)) ??
        doc.versions[0];
      return (
        relevantDoc?.answers.map((answer) => ({
          id: answer.element,
          value: answer.answer,
        })) ?? []
      );
    })
    .flat();
};

export const removeDraftDocumentVersions = (versions: DocumentVersion[]) =>
  versions.filter((version) => version.revision !== null);

export const removeDocumentVersionById = (
  versions: DocumentVersion[],
  versionId: string
) => versions.filter((version) => version.id !== versionId);

export const getLatestVersionFromDocumentVersions = (
  versions: DocumentVersion[]
): DocumentVersion | undefined => {
  return orderBy(versions, ["createdAt"], ["desc"])[0];
};

export const getLatestDocumentVersion = (
  document: Document
): DocumentVersion | undefined => {
  return getLatestVersionFromDocumentVersions(document.versions);
};

export const buildDeviceNameById = (devices: Pick<Device, "id" | "name">[]) =>
  new Map(devices.map((device) => [device.id, device.name]));

export const getDocumentCreatedBy = (documentVersion: DocumentVersion) =>
  documentVersion.createdByUser
    ? getUserDisplayName(documentVersion.createdByUser)
    : "Dovetail";

export const getNavigableDocumentLabel = (document: Document) => {
  const title = document.title?.trim();

  if (title) {
    return title;
  }

  const filename = getLatestDocumentVersion(document)?.file?.filename?.trim();
  return filename || document.id;
};

export const getLatestDocumentStatus = (document: Document): DocStatus => {
  const latestVersion = getLatestDocumentVersion(document);

  if (!latestVersion) {
    return DocumentStatus.AVAILABLE;
  }

  if (latestVersion.revision === null) {
    return latestVersion.readyForApproval
      ? DocumentStatus.UNAPPROVED
      : DocumentStatus.DRAFT;
  }

  return DocumentStatus.LIVE;
};

export const isDocumentVersionRouting = ({
  documentVersion,
  status,
}: {
  documentVersion: DocumentVersion;
  status: DocStatus;
}) =>
  status === DocumentStatus.UNAPPROVED &&
  documentVersion.documentVersionApprover.length > 0;

export const getVisibleNavigableDocuments = ({
  documents,
  resolveTemplateForDocument,
  templatesConfig,
}: {
  documents: Document[];
  resolveTemplateForDocument?: (document: Document) => Template | undefined;
  templatesConfig: TemplateConfig | undefined;
}) =>
  documents.filter((document) => {
    if (document.versions.length === 0) {
      return false;
    }

    if (!getNavigableDocumentLabel(document)) {
      return false;
    }

    if (!document.templateKey) {
      return true;
    }

    const template =
      resolveTemplateForDocument?.(document) ??
      templatesConfig?.[document.templateKey];

    return Boolean(template && !template.hidden);
  });

export const isStatusComplete = (docStatus: DocStatus | null) =>
  docStatus &&
  [DocumentStatus.LIVE, DocumentStatus.ARCHIVED].includes(docStatus);

export const getDocUIStatus = (task: RoadmapTask) => {
  if (task.enabled === false) {
    return DocumentUIStatus.LOCKED;
  }

  return;
};

export const docUIDictionary: Record<
  DocUIStatus,
  {
    label: string;
    borderColor: string;
    icon: JSX.Element;
    iconColor: IconProps["color"];
  }
> = {
  LOCKED: {
    label: "Locked",
    borderColor: "rgba(0, 0, 0, 0.87)",
    icon: (
      <LockOutlined
        color="inherit"
        data-testid="locked-icon"
        fontSize="small"
      />
    ),
    iconColor: "inherit",
  },
};

export const docStatusDictionary: Record<
  DocStatus,
  {
    label: string;
    color: ChipOwnProps["color"];
    borderColor: string;
    icon: JSX.Element;
    iconColor: IconProps["color"];
  }
> = {
  ARCHIVED: {
    label: "Archived",
    color: "default",
    borderColor: "rgba(0, 0, 0, 0.87)",
    icon: (
      <FileCopyOutlined
        color="inherit"
        data-testid="archived-icon"
        fontSize="small"
      />
    ),
    iconColor: "inherit",
  },
  DRAFT: {
    label: "Draft",
    color: "warning",
    borderColor: "rgb(237, 108, 2)",
    icon: (
      <EditOutlined color="warning" data-testid="draft-icon" fontSize="small" />
    ),
    iconColor: "warning",
  },
  UNAPPROVED: {
    label: "Unapproved",
    color: "primary",
    borderColor: "rgb(7, 78, 232)",
    icon: (
      <HistoryEdu
        color="primary"
        data-testid="generated-icon"
        fontSize="small"
      />
    ),
    iconColor: "primary",
  },
  LIVE: {
    label: "Live",
    color: "success",
    borderColor: "rgb(34, 197, 94)",
    icon: (
      <TaskOutlined color="success" data-testid="live-icon" fontSize="small" />
    ),
    iconColor: "success",
  },
  AVAILABLE: {
    label: "Available",
    color: "default",
    borderColor: "rgba(0, 0, 0, 0.87)",
    icon: (
      <NoteAddOutlined
        color="inherit"
        data-testid="available-icon"
        fontSize="small"
      />
    ),
    iconColor: "inherit",
  },
};

// Status dictionary for manual task instances
// Using string literals to avoid circular dependency with @models
export const manualTaskStatusDictionary: Record<
  "TODO" | "IN_PROGRESS" | "DONE",
  {
    label: string;
    color: ChipOwnProps["color"];
    borderColor: string;
    icon: JSX.Element;
    iconColor: IconProps["color"];
  }
> = {
  TODO: {
    label: "To Do",
    color: "default",
    borderColor: "rgba(0, 0, 0, 0.38)",
    icon: (
      <FiberManualRecord
        color="disabled"
        data-testid="todo-icon"
        fontSize="small"
      />
    ),
    iconColor: "disabled",
  },
  IN_PROGRESS: {
    label: "In Progress",
    color: "warning",
    borderColor: "rgb(237, 108, 2)",
    icon: (
      <FiberManualRecord
        color="warning"
        data-testid="in-progress-icon"
        fontSize="small"
      />
    ),
    iconColor: "warning",
  },
  DONE: {
    label: "Done",
    color: "success",
    borderColor: "rgb(34, 197, 94)",
    icon: (
      <FiberManualRecord
        color="success"
        data-testid="done-icon"
        fontSize="small"
      />
    ),
    iconColor: "success",
  },
};

type DocumentSearchOptions = {
  resolveTemplateForDocument?: (document: Document) => Template | undefined;
  templatesConfig?: TemplateConfig;
};

export const getSearchableDocumentTextParts = (
  document: Document,
  options: DocumentSearchOptions = {}
) => {
  const template = document.templateKey
    ? (options.resolveTemplateForDocument?.(document) ??
      options.templatesConfig?.[document.templateKey])
    : undefined;
  const latestVersion = getLatestDocumentVersion(document);

  return [
    getNavigableDocumentLabel(document),
    document.title,
    latestVersion?.file?.filename,
    template?.defaultTitle,
    document.templateKey,
  ].filter((value): value is string => Boolean(value?.trim()));
};

export const documentMatchesSearch = (
  document: Document,
  search: string,
  options: DocumentSearchOptions = {}
) => {
  const normalizedSearch = search.trim().toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  return getSearchableDocumentTextParts(document, options).some((value) =>
    value.toLowerCase().includes(normalizedSearch)
  );
};

export const shouldKeepDocumentDistinctInSearchResults = (
  document: Document,
  template: Template | undefined
) =>
  document.userUpload || !document.templateKey || template?.userUpload === true;

export const docsFilteredBySearch = (
  documents: Document[] | undefined,
  search: string,
  options: DocumentSearchOptions = {}
) => {
  if (!documents) return [];
  if (!search.trim()) return documents;

  return documents.filter((doc) => documentMatchesSearch(doc, search, options));
};

export const getDocConfigByElementId = (
  elementId: string,
  templatesConfig: TemplateConfig
): Template => {
  const docConfig = Object.values(templatesConfig).find((template) =>
    template.questions.some((e) => e.id === elementId)
  );

  if (!docConfig)
    throw new Error(`Document config not found for element ${elementId}`);

  return docConfig;
};

export const getAnswerType = (
  elementId: string,
  templatesConfig: TemplateConfig
): DocumentAnswerType => {
  const element = getElementConfigByDataKey(elementId, templatesConfig);

  if (!element) throw new Error(`Element with id ${elementId} not found`);

  if (element.element.type === "answerItemsElement") {
    return DocumentAnswerType.LIST;
  }
  if (element.element.type === "fileUpload") {
    return DocumentAnswerType.FILE;
  }
  return DocumentAnswerType.TEXT;
};

export const getDocumentAnswerByDataKeyId = (
  dataKeyId: string,
  documents: Document[]
) => {
  const allAnswers = documents.reduce((prev, current) => {
    return [...prev, ...(getLatestDocumentVersion(current)?.answers ?? [])];
  }, [] as DocumentAnswer[]);

  return allAnswers.find((answer) => answer.element === dataKeyId);
};

export const getDocumentAnswerValueByDataKeyId = (
  dataKeyId: string,
  documents: Document[]
) => {
  const allAnswers = documents.reduce((prev, current) => {
    return [...prev, ...(getLatestDocumentVersion(current)?.answers ?? [])];
  }, [] as DocumentAnswer[]);

  return allAnswers.find((answer) => answer.element === dataKeyId)?.answer;
};

// For a given data key id check if the document answer is part of a revision or a draft or if there is no document + version so far
export const getDocumentAndVersionForDataKeyId = (
  dataKeyId: string,
  documents: Document[],
  templatesConfig: TemplateConfig
): {
  document: Document | undefined;
  version: DocumentVersion | undefined;
} => {
  // 1. find the document that belongs to the templateKey that also holds the data key id
  const templateKey = getTemplateKeyForDataKeyId(dataKeyId, templatesConfig);
  const document =
    documents.find((doc) => doc.templateKey === templateKey) ?? undefined;

  // 2. if the document is found, check if the document answer is part of a revision or a draft
  if (document) {
    const latestVersion = getLatestDocumentVersion(document);
    if (latestVersion) {
      return {
        document,
        version: latestVersion,
      };
    }
    return {
      document: document,
      version: undefined,
    };
  }

  // 3. if the document is not found, return false. this means that the data key id is not part of any document yet
  return {
    document: undefined,
    version: undefined,
  };
};

export const getTemplateKeyForDataKeyId = (
  dataKeyId: string,
  templatesConfig: TemplateConfig
) => {
  const template = Object.values(templatesConfig).find((template) =>
    template.questions.some((e) => e.id === dataKeyId)
  );

  if (!template)
    throw new Error(`Template not found for data key ${dataKeyId}`);

  return template.templateKey;
};

export const getDocDefaultTitleByKey = (
  key: string,
  templatesConfig: TemplateConfig
): string => {
  const template = getDocTemplate(key, templatesConfig);

  if (!template) {
    throw new Error(`Template not found for key ${key}`);
  }

  return template.defaultTitle;
};

export const dedupeDocumentsById = (documents: Document[]) => {
  const documentsById = new Map<string, Document>();

  documents.forEach((document) => {
    if (!documentsById.has(document.id)) {
      documentsById.set(document.id, document);
    }
  });

  return [...documentsById.values()];
};
